/**
 * 탭 영속성 플러그인 (클라이언트 전용)
 *
 * ### 역할
 * 1. **저장** — 탭이 변경될 때마다 사용자별 localStorage 키에 자동 동기화
 * 2. **복원** — 로그인 후 사용자 정보가 확정되는 시점에 localStorage에서 이전 탭 상태 복원
 * 3. **라우팅** — 복원된 activeTabId로 라우터 이동
 * 4. **스토리지 보존** — 로그아웃 시 localStorage 데이터를 삭제하지 않아 다음 로그인 시 복원 가능
 *
 * ### localStorage 키 형식
 * `dashboard_tabs_${username}` — 사용자마다 독립된 키를 사용하여 다중 계정을 지원한다.
 *
 * ### 왜 $patch를 app:mounted로 미뤘는가?
 * 서버는 tabs=[HOME_TAB]으로 HTML을 렌더링한다.
 * 플러그인 본체에서 $patch를 호출하면 서버 HTML과 클라이언트 컴포넌트 트리가 달라
 * hydration mismatch가 발생한다. app:mounted 훅은 하이드레이션 완료 후 실행된다.
 */

import type { Tab } from '~/stores/tab'
import { useTabStore } from '~/stores/tab'
import { useAuthStore } from '~/stores/auth'
import { HOME_TAB_ID, HOME_TAB_PATH, HOME_TAB_TITLE, HOME_TAB_MSGKEY } from '~/constants'

interface PersistedTabState {
  tabs: Tab[]
  activeTabId: string
}

// 홈 탭 정의는 constants(유일 원천)에서 가져온다.
const HOME_TAB: Tab = {
  id:       HOME_TAB_ID,
  title:    HOME_TAB_TITLE,
  path:     HOME_TAB_PATH,
  closable: false,
  msgKey:   HOME_TAB_MSGKEY,
}

function storageKey(username: string): string {
  return `dashboard_tabs_${username}`
}

function saveTabs(username: string, tabs: Tab[], activeTabId: string): void {
  try {
    const payload: PersistedTabState = { tabs, activeTabId }
    localStorage.setItem(storageKey(username), JSON.stringify(payload))
  } catch (e) {
    console.warn('[TabPersist] 저장 실패:', e)
  }
}

function loadSaved(username: string): PersistedTabState | null {
  try {
    const raw = localStorage.getItem(storageKey(username))
    if (!raw) return null
    return JSON.parse(raw) as PersistedTabState
  } catch {
    return null
  }
}

function isValidTab(obj: unknown): obj is Tab {
  if (!obj || typeof obj !== 'object') return false
  const t = obj as Record<string, unknown>
  return (
    typeof t.id       === 'string'  && t.id.length > 0 &&
    typeof t.title    === 'string'  &&
    typeof t.path     === 'string'  && t.path.length > 0 &&
    typeof t.closable === 'boolean'
  )
}

export default defineNuxtPlugin((nuxtApp) => {
  const tabStore  = useTabStore()
  const authStore = useAuthStore()

  let isHydrated    = false
  // 동일 세션 내 중복 복원 방지 (app:mounted + watch 둘 다 트리거될 수 있으므로)
  let restoredForUser: string | null = null

  async function restoreTabsForUser(username: string): Promise<void> {
    if (restoredForUser === username) return
    restoredForUser = username

    const saved = loadSaved(username)
    if (!saved) {
      console.log('[TabPersist]', `저장된 탭 없음 — ${username}`)
      return
    }

    const router = useRouter()

    // 접두어(/base·/biz) 검사에 더해, 실제로 라우트가 존재하는 경로만 복원한다.
    // 메뉴 재편·화면 통합·삭제로 죽은 경로(예: 통합 전 overseas-status-recent)가
    // localStorage 에 남아 있어도, 여기서 걸러내 클릭 시 "이동 안 됨" 을 원천 차단한다.
    // catch-all 페이지가 없으므로 matched.length 로 실존을 판별할 수 있다.
    const prefixed = (saved.tabs as unknown[])
      .filter(isValidTab)
      .filter((t) => t.path === HOME_TAB.id || t.path.startsWith('/base') || t.path.startsWith('/biz'))

    const validTabs = prefixed.filter(
      (t) => t.id === HOME_TAB.id || router.resolve(t.path).matched.length > 0,
    )

    const dropped = prefixed.length - validTabs.length
    if (dropped > 0) {
      console.warn('[TabPersist]', `죽은 경로 탭 ${dropped}개 제거 — 페이지가 없는 오래된 탭`)
    }

    if (validTabs.length === 0) return

    if (!validTabs.some(t => t.id === HOME_TAB.id)) {
      validTabs.unshift({ ...HOME_TAB })
    }

    const restoredActiveId: string =
      saved.activeTabId && validTabs.some(t => t.id === saved.activeTabId)
        ? saved.activeTabId
        : HOME_TAB.id

    tabStore.$patch({ tabs: validTabs, activeTabId: restoredActiveId })
    console.log('[TabPersist]', `✅ 복원 완료 [${username}] — ${validTabs.length}개 탭 / 활성: ${restoredActiveId}`)

    const currentPath = router.currentRoute.value.path
    if (currentPath !== restoredActiveId) {
      await router.replace(restoredActiveId)
    }
  }

  // 사용자 변화 감지
  // - user → null (로그아웃): restoredForUser를 초기화해 다음 로그인 때 재복원을 허용한다.
  //   localStorage 데이터는 삭제하지 않는다 — 다음 로그인 시 복원에 사용됨.
  // - null → user (로그인): isHydrated 상태라면 즉시 복원한다.
  //   (app:mounted 이전 시점이면 app:mounted에서 처리)
  watch(
    () => authStore.user,
    async (user) => {
      if (!user) {
        restoredForUser = null
        console.log('[TabPersist] 로그아웃 — localStorage 보존, 복원 상태 초기화')
        return
      }
      if (!isHydrated) return
      await restoreTabsForUser(user.username)
    },
  )

  nuxtApp.hook('app:mounted', async () => {
    isHydrated = true

    // $subscribe — 탭 상태 변경 시마다 사용자별 localStorage에 자동 저장.
    // 로그아웃 중(user === null)에는 저장을 건너뛰어 localStorage 데이터를 보존한다.
    // (auth.ts의 logout()은 this.user = null 후 tabStore.reset()을 호출하므로
    //  $subscribe가 발화할 때 이미 user는 null이다)
    tabStore.$subscribe((_mutation, state) => {
      const username = authStore.user?.username
      if (!username) return
      saveTabs(username, state.tabs, state.activeTabId)
    }, { detached: true })

    // F5 새로고침 시나리오: auth 플러그인(app:mounted)이 먼저 실행되어
    // user가 이미 설정되어 있을 경우 여기서 즉시 복원한다.
    if (authStore.user?.username) {
      await restoreTabsForUser(authStore.user.username)
    }
  })
})
